import FontAwesome from "../../../utilities/FontAwesome";
import { isFolder, isLink } from "../../../modals/AdminModal/utilities";
import styles from "./styles/QuicklinkBlock.module.scss";
import {
  conditionType,
  currentViewType,
} from "../../../modals/AdminModal/AdminModalTypes";
import { useMemo } from "react";
import CleverSiteTreeTheme from "../../../admin/tree/CleverSiteTreeTheme";
import { numExpandedNodes } from "../../../admin/tree/Tree";
import {
  convertFlatToTree,
  convertTreeToFlat,
} from "../../../../utils/DataUtilities";
import { SortableTreeWithoutDndContext } from "@nosferatu500/react-sortable-tree";
import NavigationModel from "../../../../utils/NavigationModel";
import { useStores } from "../../../../stores/useStores";
import { Button, Flex, IconButton } from "@radix-ui/themes";

export function QuicklinkEditTree(props: {
  rowHeight?: number;
  links: Array<EditorQuickLink | Navigation>;
  updateNavigations: (
    navigations: Array<EditorQuickLink | Navigation>,
    uiOnly: boolean,
  ) => void;
  updateNavigation: (navigation: EditorQuickLink | Navigation) => void;
  createNavigation: (navigation: EditorQuickLink) => void;
  deleteNavigations: (navigations: Navigation[]) => void;
  allowSubFolders: boolean;
  parent_id?: string;
  terrace_id?: number;
}) {
  const { modalStore } = useStores();

  const { rowHeight = 60 } = props;
  const linksAsTree = props.links ? convertFlatToTree(props.links) : [];

  const height = useMemo(() => {
    return numExpandedNodes(linksAsTree) * rowHeight + 20;
  }, [numExpandedNodes(linksAsTree)]);

  function createNavigation(navigation: EditorQuickLink) {
    props.createNavigation(navigation);
  }

  async function handleNavigationsMove(obj: {
    treeData: Array<EditorQuickLink | Navigation>;
  }) {
    const basePath = props.parent_id
      ? props.parent_id.replaceAll("-", "")
      : props.terrace_id;
    props.updateNavigations(
      convertTreeToFlat(obj.treeData, String(basePath ?? "")),
      false,
    );
  }

  function handleAddNew(
    folder: boolean,
    node: Navigation | NavigationModel = new NavigationModel(),
  ) {
    node.icon = folder ? "" : "far fa-circle";

    modalStore.addModal({
      type: "admin",
      condition: node.path
        ? conditionType.ADD_SUBLINK
        : conditionType.ADD_TOPLINK,
      nodeInfo: node,
      navigation: props.links,
      parent_id: props.parent_id,
      terrace_id: props.terrace_id,
      initialView: folder ? currentViewType.folder : currentViewType.quicklink,
      navigationUpdater: (navigations) =>
        props.updateNavigations(navigations, false),
      navigationCreator: createNavigation,
    });
  }

  function canDrop(node) {
    if (props.allowSubFolders) {
      // can drop if at top level OR next parent is a folder
      return !node.nextParent || (node.nextParent && !isLink(node.nextParent));
    } else {
      // can drop if at top level OR next parent is a folder and current node is a link
      return (
        !node.nextParent ||
        (node.nextParent && !isLink(node.nextParent) && isLink(node.node))
      );
    }
  }

  return (
    <div>
      <div>
        <Flex gapX={"2"} mb={"2"}>
          <Button key="addLink" onClick={() => handleAddNew(false)}>
            <FontAwesome name={"fa-link"} prefix={"fas"} fixedWidth /> Add New
            Link
          </Button>
          <Button key="addFolder" onClick={() => handleAddNew(true)}>
            <FontAwesome name={"fa-folder-open"} prefix={"fas"} fixedWidth />{" "}
            Add New folder
          </Button>
        </Flex>
      </div>
      <div id="quick-link-form">
        <SortableTreeWithoutDndContext
          style={{ height: height }}
          treeData={linksAsTree}
          onChange={() => {}}
          canDrop={canDrop}
          onMoveNode={handleNavigationsMove}
          onVisibilityToggle={({ node, expanded }) => {
            props.updateNavigations(
              (props.links as NavigationsLinksType).map((n) => {
                if (n.path === node.path) {
                  n.expanded = expanded;
                }
                return n;
              }),
              true,
            );
          }}
          theme={CleverSiteTreeTheme}
          rowHeight={rowHeight}
          scaffoldBlockPxWidth={30}
          generateNodeProps={(rowInfo) => {
            const buttons = [
              <IconButton
                key="editLink"
                variant="ghost"
                color="gray"
                aria-label={"Edit Link"}
                title={"Edit Link"}
                onClick={() => {
                  modalStore.addModal({
                    type: "admin",
                    condition: conditionType.EDITING,
                    nodeInfo: rowInfo.node,
                    navigation: props.links,
                    initialView:
                      rowInfo.node.type === 1
                        ? currentViewType.folder
                        : undefined,
                    navigationUpdater: props.updateNavigation,
                    navigationCreator: createNavigation,
                  });
                }}
              >
                <FontAwesome prefix={"fas"} name={"fa-pen"} />
              </IconButton>,
              <IconButton
                key="delete"
                variant="ghost"
                color="red"
                onClick={async () => {
                  if (confirm("Are you sure you want to do that?")) {
                    const includeAllChildren = (
                      props.links as NavigationsLinksType
                    ).filter((item) => {
                      return (
                        item.path.slice(0, rowInfo.node.path.length) ===
                        rowInfo.node.path
                      );
                    });
                    props.deleteNavigations(includeAllChildren);
                  }
                }}
                aria-label={"Delete"}
                title={"Delete"}
              >
                <FontAwesome prefix={"fas"} name={"fa-trash-can"} />
              </IconButton>,
            ];

            const nodeIsFolder = isFolder(rowInfo.node);
            if (nodeIsFolder) {
              buttons.unshift(
                <IconButton
                  key="editNode"
                  variant="ghost"
                  color="gray"
                  aria-label={"Add Link"}
                  title={"Add Link"}
                  onClick={() => handleAddNew(false, rowInfo.node)}
                >
                  <FontAwesome prefix={"fas"} name={"fa-plus"} />
                </IconButton>,
              );
            }

            return {
              title: (
                <div className={styles.editNodeTitle}>
                  <FontAwesome
                    name={
                      nodeIsFolder ? "far fa-folder-open" : rowInfo.node.icon
                    }
                  />
                  <span>{rowInfo.node.title}</span>
                </div>
              ),
              buttons: (
                <Flex gap={"3"} align={"center"}>
                  {buttons}
                </Flex>
              ),
            };
          }}
          canNodeHaveChildren={(node) => Array.isArray(node.children)}
        />
      </div>
    </div>
  );
}
